import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Helper to call a Cloud Function with custom timeout.
 * Returns the raw `data` returned by the function.
 */
const callWithTimeout = async (funcName, data = {}, timeoutMs = 120000) => {
    const firebaseFunc = httpsCallable(functions, funcName, { timeout: timeoutMs });
    const result = await firebaseFunc(data);
    return result.data;
};

// ============================================================================
// 1. CANDIDATE PURGATORY (All 4 Servers)
// ============================================================================

/**
 * Triggers full synchronization and staging of candidates from Onboarding360 into purgatory.
 * Timeout: 9 minutes (540,000 ms).
 */
export const stageOnboardingPurgatory = (serverID) =>
    callWithTimeout(
        'stageOnboardingProductionPurgatory',
        {
            serverID,
            forceFullSync: true,
            confirmation: 'STAGE_ONBOARDING_PURGATORY',
        },
        540000
    );

/**
 * Approves a candidate purgatory item, syncing the record to live employee profile.
 * Timeout: 2 minutes (120,000 ms).
 */
export const approvePurgatoryItem = (serverID, purgatoryID) =>
    callWithTimeout(
        'approveOnboardingProductionPurgatoryItem',
        {
            serverID,
            purgatoryID,
            confirmation: 'APPROVE_ONBOARDING_PURGATORY_ITEM',
        },
        120000
    );

/**
 * Rejects a candidate purgatory item with a mandatory reason.
 * Timeout: 2 minutes (120,000 ms).
 */
export const rejectPurgatoryItem = (serverID, purgatoryID, reason) =>
    callWithTimeout(
        'rejectOnboardingProductionPurgatoryItem',
        {
            serverID,
            purgatoryID,
            reason,
        },
        120000
    );

/**
 * Refreshes an onboarding candidate's reference data.
 * Timeout: 2 minutes (120,000 ms).
 */
export const refreshCandidateReference = (serverID, purgatoryID) =>
    callWithTimeout(
        'refreshOnboardingCandidateReference',
        {
            serverID,
            purgatoryID,
        },
        120000
    );

/**
 * Previews mapping between external Onboarding360 profile and internal HA employee.
 * Timeout: 2 minutes (120,000 ms).
 */
export const previewProfileMapping = (serverID, candidateUserID) =>
    callWithTimeout(
        'previewOnboardingProductionProfileMapping',
        {
            serverID,
            candidateUserID,
        },
        120000
    );

/**
 * Previews employee suspension consequences before applying.
 * Timeout: 9 minutes (540,000 ms).
 */
export const previewEmployeeSuspension = (serverID, employeeID) =>
    callWithTimeout(
        'previewBleepEmployeeSuspension',
        {
            serverID,
            employeeID,
        },
        540000
    );

/**
 * Checks employee current suspension status and reasons.
 * Timeout: 9 minutes (540,000 ms).
 */
export const checkEmployeeSuspension = (serverID, employeeID, terminationLogID) =>
    callWithTimeout(
        'checkBleepEmployeeSuspension',
        {
            serverID,
            employeeID,
            terminationLogID,
            confirmation: `CHECK_BLEEP_SUSPENSION:${employeeID}:${terminationLogID}`,
        },
        540000
    );

// ============================================================================
// 2. CAMHS FULL PROFILE REVIEW (360camhs.co.uk only)
// ============================================================================

/**
 * Stages granular profile field diffs (addresses, emergency contacts, etc.).
 * Timeout: 3 minutes (180,000 ms).
 */
export const stageFullProfile = (serverID, purgatoryID) =>
    callWithTimeout(
        'stageOnboardingProductionFullProfile',
        {
            serverID,
            purgatoryID,
        },
        180000
    );

/**
 * Approves selected granular profile changes.
 * Timeout: 3 minutes (180,000 ms).
 */
export const approveFullProfile = (
    serverID,
    purgatoryID,
    proposalID,
    selectedPaths = [],
    dateChoices = {},
    crossDivisionConfirmation = ''
) => {
    const payload = {
        serverID,
        purgatoryID,
        proposalID,
        selectedPaths,
        dateChoices,
        confirmation: 'APPROVE_PROFILE_TRAINING_HELD',
    };
    if (crossDivisionConfirmation) {
        payload.crossDivisionConfirmation = crossDivisionConfirmation;
    }
    return callWithTimeout('approveOnboardingProductionFullProfile', payload, 180000);
};

/**
 * Retrieves the revert snapshot state for full profile changes.
 * Timeout: 2 minutes (120,000 ms).
 */
export const getProfileRevertState = (serverID, purgatoryID) =>
    callWithTimeout(
        'getOnboardingProductionProfileRevertState',
        {
            serverID,
            purgatoryID,
        },
        120000
    );

/**
 * Reverts applied full profile changes back to prior snapshot.
 * Timeout: 2 minutes (120,000 ms).
 */
export const revertFullProfile = (serverID, purgatoryID, proposalID, crossDivisionConfirmation = '') => {
    const payload = {
        serverID,
        purgatoryID,
        proposalID,
        confirmation: 'REVERT_FULL_PROFILE_TEST',
    };
    if (crossDivisionConfirmation) {
        payload.crossDivisionConfirmation = crossDivisionConfirmation;
    }
    return callWithTimeout('revertOnboardingProductionFullProfile', payload, 120000);
};

/**
 * Reviews incoming driving licence changes.
 * Timeout: 3 minutes (180,000 ms).
 */
export const reviewLicenceChange = (serverID, purgatoryID) =>
    callWithTimeout(
        'reviewOnboardingProductionLicenceChange',
        {
            serverID,
            purgatoryID,
        },
        180000
    );

/**
 * Approves driving licence change (resets DVLA validation state).
 * Timeout: 2 minutes (120,000 ms).
 */
export const approveLicenceChange = (serverID, purgatoryID, proposalID) =>
    callWithTimeout(
        'approveOnboardingProductionLicenceChange',
        {
            serverID,
            purgatoryID,
            proposalID,
            confirmation: 'APPROVE_LICENCE_NUMBER_CHANGE',
        },
        120000
    );

// ============================================================================
// 3. CAMHS TRAINING REVIEW (360camhs.co.uk only)
// ============================================================================

/**
 * Stages training courses, certificates, and validation warnings.
 * Timeout: 2 minutes (120,000 ms).
 */
export const stageTrainingReview = (serverID, purgatoryID) =>
    callWithTimeout(
        'stageOnboardingProductionTrainingReview',
        {
            serverID,
            purgatoryID,
        },
        120000
    );

/**
 * Approves selected training courses and files with custom expiry dates.
 * Timeout: 2 minutes (120,000 ms).
 */
export const approveTrainingReview = (
    serverID,
    purgatoryID,
    proposalID,
    assignments = [],
    crossDivisionConfirmation = ''
) => {
    const payload = {
        serverID,
        purgatoryID,
        proposalID,
        assignments,
        confirmation: 'APPROVE_TRAINING_ASSIGNMENTS',
    };
    if (crossDivisionConfirmation) {
        payload.crossDivisionConfirmation = crossDivisionConfirmation;
    }
    return callWithTimeout('approveOnboardingProductionTrainingReview', payload, 120000);
};

/**
 * Retrieves training review revert state and backup proposal ID.
 * Timeout: 2 minutes (120,000 ms).
 */
export const getTrainingRevertState = (serverID, purgatoryID) =>
    callWithTimeout(
        'getOnboardingProductionTrainingRevertState',
        {
            serverID,
            purgatoryID,
        },
        120000
    );

/**
 * Reverts approved training review back to the previous record.
 * Timeout: 2 minutes (120,000 ms).
 */
export const revertTrainingReview = (serverID, purgatoryID, proposalID, crossDivisionConfirmation = '') => {
    const payload = {
        serverID,
        purgatoryID,
        proposalID,
        confirmation: 'REVERT_TRAINING_ASSIGNMENTS',
    };
    if (crossDivisionConfirmation) {
        payload.crossDivisionConfirmation = crossDivisionConfirmation;
    }
    return callWithTimeout('revertOnboardingProductionTrainingReview', payload, 120000);
};

/**
 * Restores a test training record for diagnostics/repair.
 * Timeout: 2 minutes (120,000 ms).
 */
export const restoreTrainingTestRecord = (serverID = '360camhs.co.uk', purgatoryID = null) =>
    callWithTimeout(
        'restoreBleepTrainingTestRecord',
        {
            serverID,
            purgatoryID,
            confirmation: 'RESTORE_MISSING_TRAINING_TEST_RECORD',
        },
        120000
    );

// ============================================================================
// 4. CAMHS COMPLETION REVIEW (360camhs.co.uk only)
// ============================================================================

/**
 * Evaluates completion state of all sections for an onboarding candidate.
 * Timeout: 2 minutes (120,000 ms).
 */
export const reviewCompletion = (serverID, purgatoryID) =>
    callWithTimeout(
        'reviewOnboardingProductionCompletion',
        {
            serverID,
            purgatoryID,
        },
        120000
    );

/**
 * Saves granular review decisions and override reasons for sections.
 * Timeout: 2 minutes (120,000 ms).
 */
export const saveReviewDecisions = ({
    serverID,
    purgatoryID,
    assessmentFingerprint,
    confirmComplete = false,
    decisions = [],
}) =>
    callWithTimeout(
        'saveOnboardingProductionReviewDecisions',
        {
            serverID,
            purgatoryID,
            assessmentFingerprint,
            confirmComplete,
            decisions,
            confirmation: 'SAVE_ONBOARDING_REVIEW_DECISIONS',
        },
        120000
    );

// NOTE: In the backend, `invalidateOnboardingProfileCompletion` and `invalidateOnboardingTrainingCompletion`
// are automatic `onDocumentWritten` Firestore triggers, NOT callable functions.
// They execute automatically in the background when employee documents change.

// ============================================================================
// 5. EMPLOYEE INACTIVITY MAINTENANCE (All 4 Servers)
// ============================================================================

/**
 * Retrieves paginated employee inactivity evaluation report.
 * Timeout: 2 minutes (120,000 ms).
 */
export const getEmployeeInactivityReport = (serverID, runID = null, afterEmployeeID = null) => {
    const payload = { serverID };
    if (runID) payload.runID = runID;
    if (afterEmployeeID) payload.afterEmployeeID = afterEmployeeID;
    return callWithTimeout('getBleepEmployeeInactivityReport', payload, 120000);
};

/**
 * Triggers and persists a full employee inactivity assessment run.
 * Timeout: 9 minutes (540,000 ms).
 */
export const saveEmployeeInactivityReport = (serverID) =>
    callWithTimeout(
        'saveBleepEmployeeInactivityReport',
        {
            serverID,
        },
        540000
    );

/**
 * Terminates an inactive employee based on inactivity rules.
 * Timeout: 9 minutes (540,000 ms).
 */
export const terminateEmployeeForInactivity = (serverID, employeeID) =>
    callWithTimeout(
        'terminateBleepEmployeeForInactivity',
        {
            serverID,
            employeeID,
            confirmation: `TERMINATE_BLEEP_EMPLOYEE:${employeeID}`,
        },
        540000
    );

/**
 * Reverts an inactivity termination.
 * Timeout: 2 minutes (120,000 ms).
 */
export const revertEmployeeInactivityTermination = (serverID, employeeID, terminationLogID) =>
    callWithTimeout(
        'revertBleepEmployeeInactivityTermination',
        {
            serverID,
            employeeID,
            terminationLogID,
            confirmation: `REVERT_BLEEP_TERMINATION:${employeeID}:${terminationLogID}`,
        },
        120000
    );

/**
 * Executes a non-destructive dry run of employee inactivity.
 * Timeout: 9 minutes (540,000 ms).
 */
export const runEmployeeInactivityDryRun = (serverID) =>
    callWithTimeout(
        'runBleepEmployeeInactivityDryRun',
        {
            serverID,
        },
        540000
    );

// ============================================================================
// 6. MONTHLY SNAPSHOTS (360camhs.co.uk)
// ============================================================================

/**
 * Saves closed monthly snapshot of accounts and inactivity actions.
 * Timeout: 9 minutes (540,000 ms).
 */
export const saveMonthlySnapshot = (serverID, endDate = null) => {
    const payload = {
        serverID,
        confirmation: 'SAVE_MONTHLY_ACCOUNT_SNAPSHOT',
    };
    if (endDate) payload.endDate = endDate;
    return callWithTimeout('saveBleepMonthlySnapshot', payload, 540000);
};

/**
 * Retrieves monthly account snapshot report by period or cursor.
 * Supports object options { serverID, snapshotID, afterRowID } or positional args.
 * Timeout: 2 minutes (120,000 ms).
 */
export const getMonthlySnapshot = (serverID, snapshotID = '', afterRowID = '') => {
    let payload = { serverID };
    if (typeof serverID === 'object' && serverID !== null) {
        payload = { ...serverID };
    } else {
        if (snapshotID) payload.snapshotID = snapshotID;
        if (afterRowID) payload.afterRowID = afterRowID;
    }
    return callWithTimeout('getBleepMonthlySnapshot', payload, 120000);
};

export const getBleepMonthlySnapshot = getMonthlySnapshot;

// ============================================================================
// 7. DIAGNOSTICS & CANDIDATE PROFILE TOOLS
// ============================================================================

/**
 * Fetches raw candidate profile from Onboarding360 API.
 * Timeout: 2 minutes (120,000 ms).
 */
export const getCandidateProfile = (serverID, candidateUserID) =>
    callWithTimeout(
        'getOnboardingProductionCandidateProfile',
        {
            serverID,
            candidateUserID,
        },
        120000
    );

/**
 * Suspends an onboarding candidate account.
 * Timeout: 2 minutes (120,000 ms).
 */
export const suspendCandidate = (serverID, candidateUserID, status, note) =>
    callWithTimeout(
        'suspendOnboardingProductionCandidate',
        {
            serverID,
            candidateUserID,
            status,
            note,
            confirmation: 'SUSPEND_PRODUCTION_CANDIDATE',
        },
        120000
    );

// ============================================================================
// 8. HOURLY STAGING & CROSS-DIVISION INTELLIGENCE
// ============================================================================

/**
 * Retrieves the current hourly/manual staging status and review issue breakdown for a franchise.
 * Backed by `onboardingPurgatorySyncState/{serverID}` and `purgatorySyncLogs/{logID}`.
 * Timeout: 60 seconds (60,000 ms).
 */
export const getOnboardingPurgatorySyncStatus = (serverID) =>
    callWithTimeout(
        'getOnboardingPurgatorySyncStatus',
        {
            serverID,
        },
        60000
    );

/**
 * Checks cross-division review scope before applying or reverting profile/training changes.
 * Discovers if an applicant or employee exists across multiple franchises (Single-Human Principle).
 * Timeout: 60 seconds (60,000 ms).
 */
export const getOnboardingCrossDivisionReviewScope = ({
    serverID,
    purgatoryID,
    proposalID,
    kind, // 'profile' | 'training'
    operation, // 'approve' | 'revert'
}) =>
    callWithTimeout(
        'getOnboardingCrossDivisionReviewScope',
        {
            serverID,
            purgatoryID,
            proposalID,
            kind,
            operation,
        },
        60000
    );

// ============================================================================
// 9. MONTHLY LICENCE BILLING & EMAIL AUDIT (Scheduled at 2 AM on the 23rd)
// ============================================================================

/**
 * Captures or verifies baseline account history for monthly licence auditing.
 * Timeout: 9 minutes (540,000 ms).
 */
export const initializeBleepLicenceHistory = (serverID, combined = false, endDate = '') =>
    callWithTimeout(
        'initializeBleepLicenceHistory',
        {
            serverID,
            combined,
            endDate,
        },
        540000
    );

/**
 * Builds or retrieves immutable monthly licence report for a franchise or combined across all 4.
 * When combined=true, deduplicates accounts by UID so a person on 4 servers is charged once.
 * Timeout: 9 minutes (540,000 ms).
 */
export const getBleepMonthlyLicenceReport = (serverID, combined = false, endDate = '') =>
    callWithTimeout(
        'getBleepMonthlyLicenceReport',
        {
            serverID,
            combined,
            endDate,
        },
        540000
    );

/**
 * Retrieves paginated account, issue, and change rows for a monthly licence build.
 * Timeout: 3 minutes (180,000 ms).
 */
export const getBleepMonthlyLicenceRows = ({
    serverID,
    combined = false,
    endDate = '',
    checksum,
    afterRowID = '',
}) =>
    callWithTimeout(
        'getBleepMonthlyLicenceRows',
        {
            serverID,
            combined,
            endDate,
            checksum,
            afterRowID,
        },
        180000
    );

/**
 * Retrieves delivery outcomes and SMTP status for the monthly licence email job.
 * Reads from `bleepMonthlyMailJobs/{jobID}` and individual deliveries.
 * Timeout: 60 seconds (60,000 ms).
 */
export const getBleepMonthlyMailStatus = (serverID, periodID) =>
    callWithTimeout(
        'getBleepMonthlyMailStatus',
        {
            serverID,
            periodID,
        },
        60000
    );

/**
 * Previews active accounts and licence billing identity mapping across all 4 Bleep franchises.
 * Timeout: 3 minutes (180,000 ms).
 */
export const previewBleepLicenceIdentityMapping = () =>
    callWithTimeout(
        'previewBleepLicenceIdentityMapping',
        {},
        180000
    );

/**
 * Lists available historical snapshot periods.
 * Timeout: 2 minutes (120,000 ms).
 */
export const listBleepMonthlySnapshots = (serverID, afterSnapshotID = '') => {
    const payload = { serverID };
    if (afterSnapshotID) payload.afterSnapshotID = afterSnapshotID;
    return callWithTimeout('listBleepMonthlySnapshots', payload, 120000);
};

// ============================================================================
// 10. SINGLE-EMPLOYEE INACTIVITY DRILL-DOWN
// ============================================================================

/**
 * Re-evaluates inactivity for a single employee on demand.
 * Timeout: 3 minutes (180,000 ms).
 */
export const getBleepEmployeeInactivityRow = (serverID, employeeID) =>
    callWithTimeout(
        'getBleepEmployeeInactivityRow',
        {
            serverID,
            employeeID,
        },
        180000
    );

